import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Auth } from 'aws-amplify'
import { getProgramData } from '../redux/app/actions'

function idsFromOccupancyD1(d1) {
    const parts = (d1 || '').split('#')
    return {
        propertyId: parts[2],
        buildingId: parts[3],
        tenantId: parts[4],
    }
}

/**
 * When the user navigates with the browser history stack, the URL can change to another
 * admin association before Redux is updated. Detail views load from route params, but
 * the sidebar (modules) and header building selector read Redux — so they stay stale
 * until we reload program data for the occupancy that matches the current path.
 */
export default function useSyncOccupancyFromRoute() {
    const location = useLocation()
    const occupancies = useSelector((state) => state.app.occupancies)
    const tenantPk = useSelector((state) => state.app.tenant?.pk)
    const propertyPk = useSelector((state) => state.app.property?.pk)
    const buildingPk = useSelector((state) => state.app.building?.pk)
    const userD2 = useSelector((state) => state.app.user?.d2)
    const userRoles = useSelector((state) => state.app.user?.roles)
    const isLoading = useSelector((state) => state.app.isLoading)

    const inFlightRef = useRef(false)

    // Keep refs so the effect closure always reads the latest values without
    // those values being in the dependency array. This prevents the hook from
    // firing during normal forward navigation (e.g. building selector) where
    // Redux updates before history.push, which would cause a false "misaligned"
    // detection and a concurrent conflicting getProgramData call.
    const occupanciesRef = useRef(occupancies)
    const tenantPkRef = useRef(tenantPk)
    const propertyPkRef = useRef(propertyPk)
    const buildingPkRef = useRef(buildingPk)
    const userD2Ref = useRef(userD2)
    const userRolesRef = useRef(userRoles)
    const isLoadingRef = useRef(isLoading)

    useEffect(() => { occupanciesRef.current = occupancies }, [occupancies])
    useEffect(() => { tenantPkRef.current = tenantPk }, [tenantPk])
    useEffect(() => { propertyPkRef.current = propertyPk }, [propertyPk])
    useEffect(() => { buildingPkRef.current = buildingPk }, [buildingPk])
    useEffect(() => { userD2Ref.current = userD2 }, [userD2])
    useEffect(() => { userRolesRef.current = userRoles }, [userRoles])
    useEffect(() => { isLoadingRef.current = isLoading }, [isLoading])

    useEffect(() => {
        if (isLoadingRef.current || !occupanciesRef.current?.length) return

        // Super admins keep their role regardless of which property they're viewing.
        if (userRolesRef.current?.includes('SUPER_ADMIN')) return

        const path = location.pathname

        // /dashboard and /trainingportal don't carry a building ID in the URL.
        // When getProgramData navigates to these paths it embeds the occupancy d2
        // in history state so we can sync back to the right building on back/forward.
        if (path === '/dashboard' || path === '/trainingportal') {
            const d2FromState = location.state?.__occupancyD2
            if (!d2FromState) return
            if (userD2Ref.current === d2FromState) return
            const matched = occupanciesRef.current.find((o) => o.d2 === d2FromState)
            if (!matched || inFlightRef.current) return
            inFlightRef.current = true
            Auth.currentAuthenticatedUser()
                .then((user) => getProgramData({ ...matched, __fromRouteSync: true }, user))
                .finally(() => { inFlightRef.current = false })
            return
        }

        let routeId = null
        let kind = null

        const tenantMatch = path.match(/^\/admin\/tenants\/([^/]+)\/?$/)
        const propertyMatch = path.match(/^\/admin\/properties\/([^/]+)\/?$/)
        const buildingMatch = path.match(/^\/admin\/buildings\/([^/]+)\/?$/)

        if (tenantMatch) {
            routeId = tenantMatch[1]
            kind = 'tenant'
        } else if (propertyMatch) {
            routeId = propertyMatch[1]
            kind = 'property'
        } else if (buildingMatch) {
            routeId = buildingMatch[1]
            kind = 'building'
        } else {
            return
        }

        const aligned =
            (kind === 'tenant' && tenantPkRef.current === routeId) ||
            (kind === 'property' && propertyPkRef.current === routeId) ||
            (kind === 'building' && buildingPkRef.current === routeId)

        if (aligned) return

        const matched = occupanciesRef.current.find((o) => {
            const ids = idsFromOccupancyD1(o.d1)
            if (kind === 'tenant') return ids.tenantId === routeId
            if (kind === 'property') return ids.propertyId === routeId
            if (kind === 'building') return ids.buildingId === routeId
            return false
        })

        if (!matched || inFlightRef.current) return

        inFlightRef.current = true
        Auth.currentAuthenticatedUser()
            .then((user) =>
                getProgramData({ ...matched, __fromRouteSync: true }, user)
            )
            .finally(() => {
                inFlightRef.current = false
            })
    // Depend on location.key (not just pathname) so that back/forward navigation
    // between two history entries with the same path (e.g. two /dashboard entries
    // for different buildings) still triggers the sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, location.key])
}
